import { IStoragePackage } from '../features/payment/ui/buy-storage/BuyStorage.vue';
import { getApiClient } from '../services/api';
import { IEquity } from '../store/rights';
import { IDataBasic, PaginationParams, PaginationResponse, RequestConfig } from '../types/base';
import {
  BUY_TYPE,
  INVOICE_STATUS,
  INVOICE_TYPE,
  IRightsType,
  ORDER_BEHAVIOR,
  ORDER_STATUS,
  ORDER_TYPE,
  PAY_TYPE,
} from '../types/enums';
import { ICreditCoinsPackage } from './rights';

// 基础类型
export type ISO4217 = 'USD' | 'CNY' | 'HKD';

// 支付基础参数
export interface IPaymentBaseBody {
  first_name?: string;
  last_name?: string;
  email?: string;
  success_url?: string;
  cancel_url?: string;
}

// 订单列表查询参数
export type OrderListQuery = {
  many: number;
  order_type?: ORDER_TYPE;
  pay_type?: PAY_TYPE;
  status?: ORDER_STATUS;
  status__in?: string;
  o?: string;
  page?: number;
  page_size?: number;
};

// 响应类型
export type IResponse<T = any> = {
  code: number;
  data: T;
  msg?: string;
};

export type IPageResponse<T = any, D = object> = IResponse<
  {
    results: T[];
    count: number;
    next?: string;
  } & D
>;

export interface IPaginatedConfig {
  page?: number;
  page_size?: number;
  many?: number;
  o?: string;
}

// 订单实体
export interface IOrder extends IDataBasic {
  pay_id: string;
  buy_type: BUY_TYPE;
  total_storage: string;
  remained_storage: string;
  is_GB: boolean;
  id: number;
  images: string[];
  rights_id: number;
  credit_coins_package_id: number;
  no: string;
  qr_code_url: string;
  rights_name: string;
  rights_type: IRightsType;
  rights_record_id: number;
  buy_num: number;
  phone_number: string;
  behavior: number;
  parent_id: number;
  organization_id: number;
  user_id: number;
  admin_id: number;
  order_type: ORDER_TYPE;
  currency: ISO4217;
  time_identity: string;
  pay_type: PAY_TYPE;
  total_fee: number;
  free_fee: number;
  pay_fee: number;
  remark: null;
  status: ORDER_STATUS;
  has_invoice: boolean;
  invoice: string;
  is_show: boolean;
  create_time: string;
  finished_time: string;
  start_time: string;
  expired_time: string;
  update_time: string;
  is_available: boolean;
  could_apply_invoice: boolean;
  is_business_transfer: boolean;
  refund_info?: {
    refunded_success_amount: number;
    refunded_processing_amount: number;
  };
  total_credit_coin?: number;
  rights: {
    total_storage: number;
    sub_account_num: number;
    total_credit_coin: number;
    remained_credit_coin: number;
  };
  alert: number;
  other_info: {
    vendor_customer_id: string;
    airwallex_client_secret: string;
  };
  vendor_trade_no: string;
  vendor_redirect_url: string;
}

// 订阅订单
export interface ISubscription extends IOrder {
  user_info: {
    name: string;
    logo_url: string;
    phone_number: string;
    id: number;
  };
  info: Record<string, unknown>;
  unit_fee: number;
  cancel_secret: string;
  subscription_url: string;
}

// 订阅订单状态
export interface ISubscriptionStatus {
  subscription: {
    status: number;
    behavior: number;
    show_qr_code: boolean;
    pay_error: string;
  };
  order: {
    behavior: ORDER_BEHAVIOR;
    id: number;
    pay_fee: number;
    qr_code_url: string;
    status: ORDER_STATUS;
    rights_name: string;
    start_timestamp: number;
    expired_timestamp: number;
  };
}

// 言币订单
export interface ICreditCoinOrder {
  total_credit_coin: number;
  remained_credit_coin: number;
  start_time: string;
  start_timestamp: number;
  expired_time: string;
  expired_timestamp: number;
  order_type: ORDER_TYPE;
  status: 1 | 99;
  is_show: boolean;
  rights_name: string;
  rights_type: IRightsType;
  time_identity: string;
}

// 请求体类型
export interface IPostOrderClientBody {
  behavior: ORDER_BEHAVIOR;
  pay_type: PAY_TYPE;
  rights_type: IRightsType;
  time_identity: string;
}

export interface ISubscriptionCreateBody {
  rights_id: number;
  behavior: ORDER_BEHAVIOR;
}

export interface ISubscriptionBody extends IPaymentBaseBody {
  rights_id: number;
  product_url?: string;
  discount_code?: string;
  pay_type?: PAY_TYPE;
}

// 加购言币参数
export interface IPostOrderClientBuyCreditCoinsBody extends IPaymentBaseBody {
  pay_type: PAY_TYPE;
  credit_coins_package_id: number;
  num?: number;
  packageInfo?: ICreditCoinsPackage;
  parent_order_id?: number;
  order_type?: ORDER_TYPE.CREDITS;
}

export interface IPostOrderClientBuyStorageBody extends IPaymentBaseBody {
  order_type: ORDER_TYPE.STORAGE;
  pay_type: PAY_TYPE;
  num?: number;
  is_GB?: boolean;
  currentPackage?: IStoragePackage;
}

// 消耗记录
export interface IConsumptionProps extends Partial<PaginationParams> {
  user_id?: number;
}

export interface IConsumptionItem {
  id: number;
  behavior_name: string;
  name: string;
  project_id: number;
  generate_record_id: number;
  task_id: number;
  preview_id: string;
  user_id: number;
  admin_id: number;
  phone: string;
  organization_id: number;
  credit_coin: number;
  storage: number;
  owed_credit_coin: number;
  owed_storage: number;
  records_json: string;
  create_timestamp: number;
  type: number;
  behavior: number;
  c_no: string;
  status: number;
  refund_info: {
    credit_coin: number;
  };
}

export interface IConsumptionResponse {
  all_total_used_coins?: number;
  results?: IConsumptionItem[];
  total?: number;
  page?: number;
  page_size?: number;
  [key: string]: unknown;
}

// 发票相关
export interface InvoiceOrder {
  id: number;
  order_id: string;
  rights_name: string;
  ident_name: string;
  ident_code: string;
  receiver_email: string;
  invoice_type: INVOICE_TYPE;
  total_fee: number;
  status: INVOICE_STATUS;
  failed_reason: string;
  create_account_id: number;
  operate_staff_id: number;
  apply_time: string;
  finished_time: string;
  create_time: string;
  update_time: string;
}

export interface UserInvoiceInfo {
  invoice_type: INVOICE_TYPE;
  ident_name: string;
  ident_code: string;
  receiver_email: string;
  ident_name_vat?: string;
  ident_code_vat?: string;
  receiver_email_vat?: string;
  order_ids?: string[];
}

export interface InvoiceUserInfoQuery {
  order_id?: string;
}

export interface ApplyInvoicePayload {
  invoice_type: INVOICE_TYPE;
  order_ids: string[];
  ident_name: string;
  ident_code: string;
  receiver_email: string;
}

export interface InvoiceListParams {
  no_in?: string;
  many?: number;
  page?: number;
  page_size?: number;
}

export interface IOrderRefund {
  order_no: string;
  name: string;
  phone_number: string;
  refund_reason: string;
  attachments: string[];
}

// API 方法 - 常规订单
export const postOrderClient = async (data: IPostOrderClientBody, config?: RequestConfig) => {
  const client = await getApiClient();
  return client.post<IOrder>('/order/client/', data, config);
};

export const getOrderClientDetail = async (id: number) => {
  const client = await getApiClient();
  return client.get<IOrder>('/order/client/detail', { id });
};

export const getOrderList = async (params?: OrderListQuery) => {
  const client = await getApiClient();
  const res = (await client.get('/order/client/', params)) as unknown as Promise<
    PaginationResponse<IOrder>
  >;
  return res;
};

export const postOrderClientCancel = async (data: { id: number }, config?: RequestConfig) => {
  const client = await getApiClient();
  return client.post<IResponse<IOrder>>('/order/client/cancel/', data, config);
};

export const postOrderClientPayType = async (data: { id: number; pay_type: PAY_TYPE }) => {
  const client = await getApiClient();
  return client.post<IOrder>('/order/client/pay_type/', data);
};

// API 方法 - 订阅
export const getOrderClientSubscriptionStatus = async (id: number) => {
  const client = await getApiClient();
  return client.get<ISubscriptionStatus>('/order/client/subscription/status', { id });
};

export const postOrderClientSubscriptionCreate = async (
  data: ISubscriptionCreateBody,
  config?: RequestConfig,
) => {
  const client = await getApiClient();
  return client.post<ISubscription>('/order/client/subscription/create/', data, config);
};

export const postOrderClientSubscription = async (
  data: ISubscriptionBody,
  config?: RequestConfig,
) => {
  const client = await getApiClient();
  return client.post<ISubscription>('/order/client/subscription/', data, config);
};

export const getOrderClientSubscriptionMy = async () => {
  const client = await getApiClient();
  return client.get<ISubscription>('/order/client/subscription/my/');
};

export const postSubscriptionPortalPage = async (return_url: string) => {
  const client = await getApiClient();
  return client.post<IResponse<{ customer_portal_url: string }>>(
    '/order/client/subscription/portal_page/',
    return_url,
  );
};

export const deleteOrderSubscription = async () => {
  const client = await getApiClient();
  return client.delete<Promise<IResponse>>('/order/client/subscription/');
};

// API 方法 - 支付加购
export const postOrderClientBuyCreditPackage = async (
  data: IPostOrderClientBuyCreditCoinsBody,
  config?: RequestConfig,
) => {
  const client = await getApiClient();
  return client.post<IOrder>('/order/client/buy_credit_package/', data, config);
};

export const postOrderClientBuyStorage = async (
  data: IPostOrderClientBuyStorageBody,
  config?: RequestConfig,
) => {
  const client = await getApiClient();
  return client.post<IOrder>('/order/client/buy_storage/', data, config);
};

export const postOrderClientBuySubAccount = async (
  body: { pay_type: PAY_TYPE; num: number } & IPaymentBaseBody,
  config?: RequestConfig,
) => {
  const client = await getApiClient();
  return client.post<IOrder>('/order/client/buy_sub_account/', body, config);
};

export const getOrderClientUpgradePrice = async () => {
  const client = await getApiClient();
  return client.get<IEquity[]>('/order/client/upgrade_price/', {}, { autoErrorHandle: false });
};

export const getOrderClientRemainedBuyCreditCoinsCount = async () => {
  const client = await getApiClient();
  return client.get<{ remained_credit_coins: number }>(
    '/order/client/remained_buy_credit_coins_count/',
  );
};

export const getOrderCheckPurchaseLimit = async (data: { order_type: ORDER_TYPE }) => {
  const client = await getApiClient();
  return client.get<IEquity[]>('/order/client/check_purchase_limit/', data);
};

// API 方法 - 其他
export const reqCreditCoinOrder = async (params?: any) => {
  const client = await getApiClient();
  return client.get<{ order_list: ICreditCoinOrder[] }>('/order/client/my_orders/', { ...params });
};

export const consumption = async (params: IConsumptionProps) => {
  const client = await getApiClient();
  return client.get<IConsumptionResponse>('/order/consumption/client/', {
    o: '-create_time',
    many: 1,
    ...params,
  });
};

// API 方法 - 发票
export const invoiceList = async (params?: InvoiceListParams, config?: RequestConfig) => {
  const client = await getApiClient();
  return client.get<PaginationResponse<IOrder>>('/order/invoice/orders/', params, config);
};

export const getInvoice = async (params: Record<string, string | number | boolean | undefined>) => {
  const client = await getApiClient();
  return client.get<InvoiceOrder[]>('/order/invoice/', {
    ...params,
  });
};

export const getInvoiceOrders = async (params?: { no_in: string }, config?: RequestConfig) => {
  const client = await getApiClient();
  return client.get<IOrder[]>('/order/invoice/orders/', params, config);
};

export const getInvoiceLastFilled = async (
  params?: InvoiceUserInfoQuery,
  config?: RequestConfig,
) => {
  const client = await getApiClient();
  return client.get<UserInvoiceInfo[]>('/order/invoice/last_filled/', params, config);
};

export const applyInvoice = async (data: ApplyInvoicePayload, config?: RequestConfig) => {
  const client = await getApiClient();
  return client.post('/order/invoice/', data, config);
};

export const invoiceOrders = async (params?: any, config?: RequestConfig) => {
  const client = await getApiClient();
  return client.get<PaginationResponse<InvoiceOrder>>('/order/invoice/', params, config);
};

// API 方法 - 退款
export const postOrderRefund = async (data: IOrderRefund) => {
  const client = await getApiClient();
  return client.post<InvoiceOrder[]>('/order/client/refund/', {
    ...data,
  });
};

export const getOrderRefundAvailableOrders = async (data: IPaginatedConfig) => {
  const client = await getApiClient();
  return client.get<PaginationResponse<IOrder>>('/order/client/refund_available_orders/', {
    ...data,
  });
};
